import {Box, HBox} from '../../../../../../src/components/core/view';
import React, {useMemo} from 'react';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../../../src/appStyles/hooks/theme';

import {DeliveryLocationStatus} from '../../../../../../src/driver/types/common';
import {H6Text} from '../../../../../../src/components/core/text';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {StyleSheet} from 'react-native';
import {useGetLocale} from '@buncha/localization/hooks';

interface OrderStatusCellProps {
  deliveryLocationStatus: DeliveryLocationStatus | string;
  runId: number;
  orderId: number;
  externalId: string;
}

const OrderStatusCell: React.FC<OrderStatusCellProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const runId = props?.runId || 0;
  const orderId = props?.orderId || 0;
  const {body, button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const getStatusText = useMemo(() => {
    switch (props?.deliveryLocationStatus) {
      case DeliveryLocationStatus.driverAssigned:
        return button?.orderPacked;
      case DeliveryLocationStatus.onTheWay:
        return button?.inTransit;
      case DeliveryLocationStatus.delivered:
        return button?.delivered;
      default:
        return button?.orderPacked;
    }
  }, [
    button?.delivered,
    button?.inTransit,
    button?.orderPacked,
    props?.deliveryLocationStatus,
  ]);

  const getColors = useMemo(() => {
    switch (props?.deliveryLocationStatus) {
      case DeliveryLocationStatus.driverAssigned:
        return {
          background: theme?.background?.gray?.[100],
          foreground: theme?.text?.gray?.[100],
        };
      case DeliveryLocationStatus.onTheWay:
        return {
          background: theme?.background?.blue?.[100],
          foreground: theme?.background?.blue?.[100],
        };
      case DeliveryLocationStatus.delivered:
        return {
          background: theme?.background?.green[100],
          foreground: theme?.text?.green[100],
        };

      default:
        return {
          background: theme?.background?.gray?.[200],
          foreground: theme?.text?.gray?.[100],
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.deliveryLocationStatus]);
  return (
    <HBox style={styles.parentBox}>
      <HBox
        style={[
          styles.statusCell,
          styles.borderWidth,
          {borderColor: getColors?.background},
        ]}>
        <Box
          style={[
            styles.dot,
            {
              backgroundColor: getColors?.foreground,
            },
          ]}
        />
        <H6Text
          style={{
            fontFamily: muli?.[700]?.normal,
            color: getColors?.foreground,
          }}>
          {getStatusText}
        </H6Text>
      </HBox>
      <Box>
        <Box
          style={[
            styles.statusCell,
            {backgroundColor: theme?.background?.gray?.[200]},
          ]}>
          <H6Text style={{fontFamily: muli?.[700]?.normal}}>
            {body?.runID}
            {runId}
          </H6Text>
        </Box>
        <Box
          style={[
            styles.statusCell,
            {backgroundColor: theme?.background?.gray?.[200]},
          ]}>
          <H6Text style={{fontFamily: muli?.[700]?.normal}}>
            {body?.ordID}
            {orderId}
          </H6Text>
        </Box>
        <If condition={props.externalId}>
          <Box
            style={[
              styles.statusCell,
              {backgroundColor: theme?.background?.gray?.[200]},
            ]}>
            <H6Text style={{fontFamily: muli?.[700]?.normal}}>
              #{props.externalId}
            </H6Text>
          </Box>
        </If>
      </Box>
    </HBox>
  );
};

export default React.memo(OrderStatusCell);

const styles = StyleSheet.create({
  parentBox: {
    alignItems: 'flex-start',
  },
  statusCell: {
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginBottom: 2,
  },
  borderWidth: {
    borderWidth: 1,
  },
  runIdCell: {
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  dot: {
    height: 8,
    width: 8,
    borderRadius: 50,
    marginRight: 5,
  },
});
